import React from 'react';
import { 
  Box, Typography, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, Chip,
  Drawer, Button, TextField, Divider, IconButton, CircularProgress, Alert
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useNotification } from '../components/layouts/Layout';
import { useAuth } from '../contexts/AuthContext';

type RequestStatus = 'złożony' | 'zgoda' | 'odrzucony';

interface RequestRow {
  id?: number;
  request_id: string;
  request_date: string;
  applicant_full_name: string;
  title: 'Wniosek o urlop' | 'Wniosek o zaliczkę';
  details?: string;
  status: RequestStatus;
  generated_pdf_url?: string | null;
  date_from?: string;
  date_to?: string;
  days?: number;
}

const headerCellSx: any = {
  backgroundColor: (t: any) => (t.palette.mode === 'light' ? '#f5f5f5' : 'rgba(255,255,255,0.06)'),
  fontWeight: 600,
  color: (t: any) => t.palette.text.secondary,
  position: 'sticky',
  top: 0,
  zIndex: 2
};

const cellSx: any = { padding: '6px 8px', fontSize: '0.9rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const formatDate = (isoOrYmd: string): string => {
  try {
    // allow YYYY-MM-DD or ISO
    const d = isoOrYmd.length === 10 ? new Date(isoOrYmd + 'T12:00:00Z') : new Date(isoOrYmd);
    return new Intl.DateTimeFormat('pl-PL').format(d);
  } catch {
    return isoOrYmd;
  }
};

const statusChip = (status: RequestStatus) => {
  switch (status) {
    case 'zgoda':
      return <Chip label="zgoda" color="success" size="small" variant="outlined" />;
    case 'odrzucony':
      return <Chip label="odrzucony" color="error" size="small" variant="outlined" />;
    default:
      return <Chip label="złożony" color="info" size="small" variant="outlined" />;
  }
};

const RequesterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotification();
  const { user } = useAuth();

  const [rows, setRows] = React.useState<RequestRow[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [selectedRequest, setSelectedRequest] = React.useState<RequestRow | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState<boolean>(false);
  const [decisionRemarks, setDecisionRemarks] = React.useState<string>('');
  const [processingDecision, setProcessingDecision] = React.useState<boolean>(false);

  // Sprawdź czy użytkownik ma uprawnienia do akceptacji (admin lub księgowość)
  const canApprove = React.useMemo(() => {
    if (!user) return false;
    const role = String(user.role || '').toLowerCase();
    if (role === 'admin') return true;
    const permissions = user.permissions || {};
    return Boolean(permissions.accounting) || Boolean(permissions['księgowość']) || Boolean(permissions.ksiegowosc);
  }, [user]);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/requester/all-leave-requests');
        if (!mounted) return;
        const list = (res?.data?.data || []) as RequestRow[];
        setRows(list);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('[RequesterPage] load failed', e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Obsługa parametru URL ?request=id
  React.useEffect(() => {
    const requestId = searchParams.get('request');
    if (requestId && rows.length > 0) {
      const request = rows.find(r => r.id === Number(requestId));
      if (request) {
        setSelectedRequest(request);
        setDrawerOpen(true);
      }
    }
  }, [searchParams, rows]);

  const handleRowClick = (request: RequestRow) => {
    setSelectedRequest(request);
    setDrawerOpen(true);
    setDecisionRemarks('');
    setSearchParams({ request: String(request.id) });
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setSelectedRequest(null);
    setDecisionRemarks('');
    setSearchParams({});
  };

  const handleDecision = async (decision: 'zgoda' | 'odrzucony') => {
    if (!selectedRequest || !selectedRequest.id) return;
    
    setProcessingDecision(true);
    try {
      await api.patch(`/requester/leave-requests/${selectedRequest.id}/decision`, {
        decision,
        remarks: decisionRemarks || null
      });
      
      showNotification(
        `Wniosek ${decision === 'zgoda' ? 'zaakceptowany' : 'odrzucony'}`,
        'success'
      );

      // Odśwież listę wniosków
      const res = await api.get('/requester/all-leave-requests');
      const list = (res?.data?.data || []) as RequestRow[];
      setRows(list);
      
      // Zaktualizuj wybrany wniosek
      const updated = list.find(r => r.id === selectedRequest.id);
      if (updated) {
        setSelectedRequest(updated);
      }

      // Jeśli wniosek został rozpatrzony, zamknij drawer po 2 sekundach
      if (updated && updated.status !== 'złożony') {
        setTimeout(() => {
          handleCloseDrawer();
        }, 2000);
      }
    } catch (e: any) {
      showNotification(
        e?.response?.data?.message || 'Błąd podczas przetwarzania decyzji',
        'error'
      );
    } finally {
      setProcessingDecision(false);
    }
  };

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      {/* Nagłówek w stylu stron listowych */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0,
            mt: '8.5px'
          }}
        >
          Wnioski pracownicze
        </Typography>
      </Box>

      {/* Tabela na wzór ofert/projektów */}
      <TableContainer component={Paper} sx={{ boxShadow: 'none', border: (t) => `1px solid ${t.palette.divider}` }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellSx, width: 140 }}>Data wniosku</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 220 }}>Id wniosku</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 220 }}>Wnioskodawca</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 220 }}>Tytuł wniosku</TableCell>
              <TableCell sx={{ ...headerCellSx }}>Szczegóły wniosku</TableCell>
              <TableCell sx={{ ...headerCellSx, width: 160 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  Brak wniosków
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow 
                  key={r.request_id || `${r.request_date}-${r.applicant_full_name}`} 
                  hover 
                  sx={{ '&:hover': { backgroundColor: '#F5F5FF', cursor: 'pointer' } }}
                  onClick={() => handleRowClick(r)}
                >
              <TableCell sx={cellSx}>{formatDate(r.request_date)}</TableCell>
              <TableCell sx={cellSx}>
                {r.generated_pdf_url ? (
                  <a href={r.generated_pdf_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>{r.request_id}</a>
                ) : r.request_id}
              </TableCell>
              <TableCell sx={cellSx}>{r.applicant_full_name}</TableCell>
                  <TableCell sx={cellSx}>{r.title}</TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 520 }}>{r.details}</TableCell>
                  <TableCell sx={cellSx}>{statusChip(r.status)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Drawer z podglądem wniosku i panelem akceptacji */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: 600,
            maxWidth: '90vw'
          }
        }}
      >
        {selectedRequest && (
          <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Nagłówek */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500 }}>
                Podgląd wniosku
              </Typography>
              <IconButton 
                onClick={handleCloseDrawer} 
                size="small"
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 30,
                  minHeight: 30,
                  '& > *': {
                    pointerEvents: 'none',
                  },
                }}
              >
                <Box 
                  component="img" 
                  src="/assets/close.svg" 
                  alt="Zamknij" 
                  sx={{ 
                    width: 13, 
                    height: 13, 
                    display: 'block',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                  }} 
                />
              </IconButton>
            </Box>

            {/* Szczegóły wniosku */}
            <Box sx={{ flex: 1, overflow: 'auto', mb: 3 }}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>Numer wniosku</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{selectedRequest.request_id}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>Data złożenia</Typography>
                <Typography variant="body1">{formatDate(selectedRequest.request_date)}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>Wnioskodawca</Typography>
                <Typography variant="body1">{selectedRequest.applicant_full_name}</Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>Tytuł</Typography>
                <Typography variant="body1">{selectedRequest.title}</Typography>
              </Box>

              {selectedRequest.date_from && selectedRequest.date_to && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>Okres urlopu</Typography>
                  <Typography variant="body1">
                    {formatDate(selectedRequest.date_from)} - {formatDate(selectedRequest.date_to)}
                    {selectedRequest.days && ` (${selectedRequest.days} ${selectedRequest.days === 1 ? 'dzień' : 'dni'})`}
                  </Typography>
                </Box>
              )}

              {selectedRequest.details && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>Szczegóły</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{selectedRequest.details}</Typography>
                </Box>
              )}

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>Status</Typography>
                {statusChip(selectedRequest.status)}
              </Box>

              {selectedRequest.generated_pdf_url && (
                <Box sx={{ mb: 2 }}>
                  <Button
                    variant="outlined"
                    href={selectedRequest.generated_pdf_url}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ textTransform: 'none' }}
                  >
                    Otwórz PDF
                  </Button>
                </Box>
              )}
            </Box>

            {/* Panel akceptacji - tylko dla admin i księgowość, tylko dla wniosków w statusie "złożony" */}
            {canApprove && selectedRequest.status === 'złożony' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, mb: 2 }}>
                    Panel akceptacji
                  </Typography>
                  
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Uwagi (opcjonalnie)"
                    value={decisionRemarks}
                    onChange={(e) => setDecisionRemarks(e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={processingDecision}
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => handleDecision('zgoda')}
                      disabled={processingDecision}
                      sx={{ flex: 1, textTransform: 'none' }}
                    >
                      {processingDecision ? <CircularProgress size={24} color="inherit" /> : 'Zgoda'}
                    </Button>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleDecision('odrzucony')}
                      disabled={processingDecision}
                      sx={{ flex: 1, textTransform: 'none' }}
                    >
                      {processingDecision ? <CircularProgress size={24} color="inherit" /> : 'Odrzucony'}
                    </Button>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        )}
      </Drawer>
    </Box>
  );
};

export default RequesterPage;


